import { keepPreviousData, useQuery } from '@tanstack/react-query';
import http from '../../../utils/http';
import { ReportResponse } from '../types';
import { useTranslation } from 'utils/translation';
import { useUiVersion } from '../../ui/UserInterfaceVersionProvider';

type Args = {
  id: string;
  params?: object;
  page?: number;
  perPage?: number;
  sortableField?: string | null;
  sortableDirection?: string | null;
  queryKeyPrefix?: string;
};

const useReport = ({
  id,
  page,
  perPage,
  sortableField,
  sortableDirection,
  params = {},
  queryKeyPrefix = '',
}: Args) => {
  const { i18n } = useTranslation();
  const { version } = useUiVersion();
  const url = version === 'v2' ? `/api/reports/v2/${id}` : `/api/reports/${id}`;
  const paramsKey = JSON.stringify(params);

  return useQuery({
    queryKey: [
      queryKeyPrefix,
      version,
      id,
      'report_results',
      i18n.language,
      paramsKey,
      page,
      perPage,
      sortableField,
      sortableDirection,
    ],
    queryFn: () =>
      http.get<never, ReportResponse>(url, {
        params: {
          page,
          perPage,
          pageSize: perPage,
          order: sortableField,
          direction: sortableDirection,
          params: paramsKey,
        },
      }),
    enabled: Boolean(id),
    placeholderData: keepPreviousData,
    refetchInterval: false,
  });
};

export const useReportTotalResults = ({ id, params = {} }: Args) => {
  const { i18n } = useTranslation();
  const paramsKey = JSON.stringify(params);

  return useQuery({
    queryKey: [id, i18n.language, 'report_results_total_results', paramsKey],
    queryFn: () =>
      http.get<never, { total: number }>(`/api/reports/${id}/total`, {
        params: {
          params: paramsKey,
        },
      }),
    enabled: Boolean(id),
    placeholderData: keepPreviousData,
    refetchInterval: false,
  });
};

export default useReport;
